/**
 * Return a type that represents a union of all the values in the object.
 * Set up for use with javascript objects that represent enums
 */
export type ObjectValues<T> = T[keyof T];

/**
 * Return a type that represents a union of all the values in an array.
 * Set up for use with javascript arrays that represent enums, after returning the values with Object.values
 */
export type ArrayValues<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

/**
 * Convert an array of key-value pairs into an object.
 * Set up for use with Object.entries, which returns an array of key-value pairs.
 */
export const objectFromEntries = <
  const T extends ReadonlyArray<readonly [PropertyKey, unknown]>,
>(
  entries: T
): { [K in T[number] as K[0]]: K[1] } => {
  // This is a specific case where the type inference is not sufficient,
  // and we know that the entries are valid key-value pairs.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return Object.fromEntries(entries) as { [K in T[number] as K[0]]: K[1] };
};

export function toEnum<T extends Record<string, string | number>>(
  value: string | null | undefined,
  enumType: T
): T[keyof T] {
  if (!value) {
    throw new Error(`Input must be defined and non-empty JSON object`);
  }

  if (Object.values(enumType).includes(value as T[keyof T])) {
    return value as T[keyof T];
  }

  throw new Error(`Incompatible enum. Value does not match any enum value`);
}